// API Client for BookForge 2.0 - Universal Items System

let RUNTIME_API_BASE = null;
const getApiBase = async () => {
  if (RUNTIME_API_BASE) return RUNTIME_API_BASE;
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (res.ok) {
      const cfg = await res.json();
      if (cfg && typeof cfg.API_BASE === 'string' && cfg.API_BASE.length > 0) {
        RUNTIME_API_BASE = cfg.API_BASE;
        return RUNTIME_API_BASE;
      }
    }
  } catch {}
  RUNTIME_API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
  return RUNTIME_API_BASE;
};

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('authToken');
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  try {
    const base = await getApiBase();
    const response = await fetch(`${base}${endpoint}`, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    // Gracefully handle empty responses (e.g., 204 No Content) or non-JSON bodies
    if (response.status === 204) {
      return null;
    }
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await response.text().catch(() => '');
      return text || null;
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
}

// Universal Items API
export const Items = {
  // List all items with optional filters
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/items${query}`);
  },

  // Get item by ID
  get: (id) => apiCall(`/items/${id}`),

  // Get item by ISBN
  getByIsbn: (isbn) => apiCall(`/items/isbn/${isbn}`),

  // Get item by GTIN
  getByGtin: (gtin) => apiCall(`/items/gtin/${gtin}`),

  // Create new item
  create: (itemData) => apiCall('/items', {
    method: 'POST',
    body: JSON.stringify(itemData),
  }),

  // Update existing item
  update: (id, itemData) => apiCall(`/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(itemData),
  }),

  // Delete item
  delete: (id) => apiCall(`/items/${id}`, {
    method: 'DELETE',
  }),

  // Get item components
  getComponents: (id) => apiCall(`/items/${id}/components`),

  // Add component to item
  addComponent: (id, componentData) => apiCall(`/items/${id}/components`, {
    method: 'POST',
    body: JSON.stringify(componentData),
  }),

  // Remove component from item
  removeComponent: (componentId) => apiCall(`/items/components/${componentId}`, {
    method: 'DELETE',
  }),
};

// Helper to read auth token from localStorage or cookie
function getAuthToken() {
  try {
    const ls = localStorage.getItem('authToken');
    if (ls) return ls;
    if (typeof document !== 'undefined') {
      const match = document.cookie.match(/(?:^|; )auth-token=([^;]+)/);
      if (match) return decodeURIComponent(match[1]);
    }
  } catch (_) {}
  return null;
}

// Uploads API
export const Uploads = {
  upload: async (file) => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    const base = await getApiBase();
    const response = await fetch(`${base}/uploads/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    body: formData,
    credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  },
};

// Inventory Movements API
export const Inventory = {
  // List inventory movements
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/inventory${query}`);
  },

  // Get movement by ID
  get: (id) => apiCall(`/inventory/${id}`),

  // Get movement with item details
  getWithItems: (id) => apiCall(`/inventory/${id}/with-items`),

  // Create new movement
  create: (movementData) => apiCall('/inventory', {
    method: 'POST',
    body: JSON.stringify(movementData),
  }),

  // Update movement
  update: (id, movementData) => apiCall(`/inventory/${id}`, {
    method: 'PUT',
    body: JSON.stringify(movementData),
  }),

  // Delete movement
  delete: (id) => apiCall(`/inventory/${id}`, {
    method: 'DELETE',
  }),

  // Complete movement
  complete: (id) => apiCall(`/inventory/${id}/complete`, {
    method: 'POST',
  }),

  // Cancel movement
  cancel: (id) => apiCall(`/inventory/${id}/cancel`, {
    method: 'POST',
  }),

  // Get stock levels
  getStockLevels: (warehouseId = null) => {
    const query = warehouseId ? `?warehouse_id=${warehouseId}` : '';
    return apiCall(`/inventory/stock/levels${query}`);
  },

  // Get movement history for item
  getMovementHistory: (itemId, warehouseId = null) => {
    const query = warehouseId ? `?warehouse_id=${warehouseId}` : '';
    return apiCall(`/inventory/stock/history/${itemId}${query}`);
  },
};

// Print Runs API
export const PrintRuns = {
  // List print runs
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/print-runs${query}`);
  },

  // Get print run by ID
  get: (id) => apiCall(`/print-runs/${id}`),

  // Get print run with item details
  getWithItem: (id) => apiCall(`/print-runs/${id}/with-item`),

  // Create new print run
  create: (printRunData) => apiCall('/print-runs', {
    method: 'POST',
    body: JSON.stringify(printRunData),
  }),

  // Update print run
  update: (id, printRunData) => apiCall(`/print-runs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(printRunData),
  }),

  // Delete print run
  delete: (id) => apiCall(`/print-runs/${id}`, {
    method: 'DELETE',
  }),

  // Start print run
  start: (id) => apiCall(`/print-runs/${id}/start`, {
    method: 'POST',
  }),

  // Complete print run
  complete: (id, actualQuantity) => apiCall(`/print-runs/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify({ actual_quantity: actualQuantity }),
  }),

  // Cancel print run
  cancel: (id) => apiCall(`/print-runs/${id}/cancel`, {
    method: 'POST',
  }),

  // Get print runs by item
  getByItem: (itemId) => apiCall(`/print-runs/item/${itemId}`),

  // Get print run statistics
  getStats: (period = 'month', itemId = null) => {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (itemId) params.append('item_id', itemId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/print-runs/stats/summary${query}`);
  },

  // Get print run history for item
  getHistory: (itemId, limit = 10) => {
    const query = `?limit=${limit}`;
    return apiCall(`/print-runs/history/${itemId}${query}`);
  },
};

// App Settings API
export const AppSettings = {
  // List all app settings
  list: () => apiCall('/settings'),

  // Get first app settings with defaults
  get: () => apiCall('/settings/first'),

  // Get app settings by ID
  getById: (id) => apiCall(`/settings/${id}`),

  // Create new app settings
  create: (data) => apiCall('/settings', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Update app settings
  update: (id, data) => apiCall(`/settings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // Delete app settings
  delete: (id) => apiCall(`/settings/${id}`, {
    method: 'DELETE',
  }),

  // Initialize default settings
  initialize: () => apiCall('/settings/initialize', {
    method: 'POST',
  }),

  // Get app settings statistics
  getStats: () => apiCall('/settings/stats/overview'),
  sendTelegramTestMessage: () => apiCall('/settings/telegram/test-message', {
    method: 'POST',
  }),
};

// Dashboard API
export const Dashboard = {
  getKpis: () => apiCall('/dashboard/kpis'),
  getRecentOrders: (limit = 5) => apiCall(`/dashboard/recent-orders?limit=${limit}`),
  getDashboardData: () => apiCall('/dashboard/data'),
};

// Legacy APIs for backward compatibility
export const Books = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/books${query}`);
  },

  get: (id) => apiCall(`/books/${id}`),
  create: (bookData) => apiCall('/books', {
    method: 'POST',
    body: JSON.stringify(bookData),
  }),
  update: (id, bookData) => apiCall(`/books/${id}`, {
    method: 'PUT',
    body: JSON.stringify(bookData),
  }),
  delete: (id) => apiCall(`/books/${id}`, {
    method: 'DELETE',
  }),
};

// Alias for backward compatibility
export const Book = Books;

export const BookEditions = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    // Фильтруем только book_edition типы
    params.append('type', 'book_edition');
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/items${query}`);
  },

  get: (id) => apiCall(`/items/${id}`),
  getByIsbn: (isbn) => apiCall(`/items/isbn/${isbn}`),
  create: (editionData) => apiCall('/items', {
    method: 'POST',
    body: JSON.stringify(editionData),
  }),
  update: (id, editionData) => apiCall(`/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(editionData),
  }),
  delete: (id) => apiCall(`/book-editions/delete/${id}`, {
    method: 'POST',
  }),
};

export const Products = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/items${query}`);
  },

  get: (id) => apiCall(`/items/${id}`),
  create: (productData) => apiCall('/items', {
    method: 'POST',
    body: JSON.stringify(productData),
  }),
  update: (id, productData) => apiCall(`/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(productData),
  }),
  delete: (id) => apiCall(`/items/${id}`, {
    method: 'DELETE',
  }),
};

// Alias for backward compatibility
export const Product = Products;

export const ProductComponents = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/items${query}`);
  },

  get: (id) => apiCall(`/items/${id}`),
  create: (componentData) => apiCall('/items', {
    method: 'POST',
    body: JSON.stringify(componentData),
  }),
  update: (id, componentData) => apiCall(`/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(componentData),
  }),
  delete: (id) => apiCall(`/items/${id}`, {
    method: 'DELETE',
  }),
};

export const Warehouses = {
  list: () => apiCall('/warehouses'),
  get: (id) => apiCall(`/warehouses/${id}`),
  create: (warehouseData) => apiCall('/warehouses', {
    method: 'POST',
    body: JSON.stringify(warehouseData),
  }),
  update: (id, warehouseData) => apiCall(`/warehouses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(warehouseData),
  }),
  delete: (id) => apiCall(`/warehouses/${id}`, {
    method: 'DELETE',
  }),
};

// Alias for backward compatibility
export const Warehouse = Warehouses;

export const Users = {
  list: () => apiCall('/users'),
  get: (id) => apiCall(`/users/${id}`),
  me: () => apiCall('/auth/me'),
  create: (userData) => apiCall('/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),
  update: (id, userData) => apiCall(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  }),
  delete: (id) => apiCall(`/users/${id}`, {
    method: 'DELETE',
  }),
};

// Alias for backward compatibility
export const User = Users;

// Additional legacy APIs for backward compatibility
export const Order = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/orders${query}`);
  },
  get: (id) => apiCall(`/orders/${id}`),
  create: (orderData) => apiCall('/orders', {
    method: 'POST',
    body: JSON.stringify(orderData),
  }),
  update: (id, orderData) => apiCall(`/orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(orderData),
  }),
  delete: (id) => apiCall(`/orders/${id}`, {
    method: 'DELETE',
  }),
  restore: (id) => apiCall(`/orders/${id}/restore`, {
    method: 'POST',
  }),
  retryCreateShipmentOrder: (id) => apiCall(`/orders/${id}/retry-create-shipment-order`, {
    method: 'POST',
  }),
};

export const Customer = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/customers${query}`);
  },
  get: (id) => apiCall(`/customers/${id}`),
  create: (customerData) => apiCall('/customers', {
    method: 'POST',
    body: JSON.stringify(customerData),
  }),
  update: (id, customerData) => apiCall(`/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(customerData),
  }),
  delete: (id) => apiCall(`/customers/${id}`, {
    method: 'DELETE',
  }),
};

export const OrderHistory = {
  get: (orderId) => apiCall(`/orders/${orderId}/history`),
};

export const InventoryMovement = {
  // List inventory movements
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/inventory${query}`);
  },

  // Get inventory movement by ID
  get: (id) => apiCall(`/inventory/${id}`),

  // Create new inventory movement
  create: (movementData) => apiCall('/inventory', {
    method: 'POST',
    body: JSON.stringify(movementData),
  }),

  // Update inventory movement
  update: (id, movementData) => apiCall(`/inventory/${id}`, {
    method: 'PUT',
    body: JSON.stringify(movementData),
  }),

  // Delete inventory movement
  delete: (id) => apiCall(`/inventory/${id}`, {
    method: 'DELETE',
  }),

  // Get stock balances for a specific date
  getStockBalances: (date = null, warehouseId = null) => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (warehouseId) params.append('warehouse_id', warehouseId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/inventory/stock${query}`);
  },
};

export const InventoryMovementItem = {
  list: () => apiCall('/inventory/items'),
  get: (id) => apiCall(`/inventory/items/${id}`),
  create: (itemData) => apiCall('/inventory/items', {
    method: 'POST',
    body: JSON.stringify(itemData),
  }),
  update: (id, itemData) => apiCall(`/inventory/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(itemData),
  }),
  delete: (id) => apiCall(`/inventory/items/${id}`, {
    method: 'DELETE',
  }),
};

export const InventoryMovementHistory = {
  get: (warehouseId) => apiCall(`/warehouses/${warehouseId}/movement-history`),
  create: (data) => apiCall('/inventory/history', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/inventory/history${query}`);
  },
};

export const BookEdition = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    // Фильтруем только book_edition типы
    params.append('type', 'book_edition');
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/items${query}`);
  },
  get: (id) => apiCall(`/items/${id}`),
  create: (data) => apiCall('/items', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/items/${id}`, {
    method: 'DELETE',
  }),
  getByBook: (bookId) => apiCall(`/items/book/${bookId}`),
  getByIsbn: (isbn) => apiCall(`/items/isbn/${isbn}`),
  search: (term) => apiCall(`/items/search/${encodeURIComponent(term)}`),
};

export const ProductComponent = {
  list: () => apiCall('/product-components'),
  filter: (criteria = {}) => {
    const params = new URLSearchParams();
    Object.entries(criteria).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/product-components${query}`);
  },

  get: (productId) => apiCall(`/product-components/product/${productId}`),
  getById: (id) => apiCall(`/product-components/${id}`),
  create: (data) => apiCall('/product-components', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/product-components/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/product-components/${id}`, {
    method: 'DELETE',
  }),
  add: (productId, data) => apiCall('/product-components', {
    method: 'POST',
    body: JSON.stringify({ ...data, product_id: productId }),
  }),
  remove: (productId, componentId) => apiCall(`/product-components/${componentId}`, {
    method: 'DELETE',
  }),
  bulkCreate: (components) => apiCall('/product-components/bulk', {
    method: 'POST',
    body: JSON.stringify({ components }),
  }),
  replaceByProductId: (productId, components) => apiCall(`/product-components/product/${productId}/replace`, {
    method: 'PUT',
    body: JSON.stringify({ components }),
  }),
  deleteByProductId: (productId) => apiCall(`/product-components/product/${productId}`, {
    method: 'DELETE',
  }),
};

// Additional legacy APIs for backward compatibility
export const Cycle = {
  list: () => apiCall('/cycles'),
  get: (id) => apiCall(`/cycles/${id}`),
  create: (cycleData) => apiCall('/cycles', {
    method: 'POST',
    body: JSON.stringify(cycleData),
  }),
  update: (id, cycleData) => apiCall(`/cycles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(cycleData),
  }),
  delete: (id) => apiCall(`/cycles/${id}`, {
    method: 'DELETE',
  }),
};

export const PrintRun = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/print-runs${query}`);
  },
  get: (id) => apiCall(`/print-runs/${id}`),
  create: (printRunData) => apiCall('/print-runs', {
    method: 'POST',
    body: JSON.stringify(printRunData),
  }),
  update: (id, printRunData) => apiCall(`/print-runs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(printRunData),
  }),
  delete: (id) => apiCall(`/print-runs/${id}`, {
    method: 'DELETE',
  }),
};

export const PrintRunHistory = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/print-runs/history${query}`);
  },
  get: (id) => apiCall(`/print-runs/history/${id}`),
  create: (historyData) => apiCall('/print-runs/history', {
    method: 'POST',
    body: JSON.stringify(historyData),
  }),
  bulkCreate: (historyDataArray) => apiCall('/print-runs/history/bulk', {
    method: 'POST',
    body: JSON.stringify(historyDataArray),
  }),
  update: (id, historyData) => apiCall(`/print-runs/history/${id}`, {
    method: 'PUT',
    body: JSON.stringify(historyData),
  }),
  delete: (id) => apiCall(`/print-runs/history/${id}`, {
    method: 'DELETE',
  }),
};

export const Waitlist = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/waitlist${query}`);
  },
  get: (id) => apiCall(`/waitlist/${id}`),
  create: (waitlistData) => apiCall('/waitlist', {
    method: 'POST',
    body: JSON.stringify(waitlistData),
  }),
  update: (id, waitlistData) => apiCall(`/waitlist/${id}`, {
    method: 'PUT',
    body: JSON.stringify(waitlistData),
  }),
  delete: (id) => apiCall(`/waitlist/${id}`, {
    method: 'DELETE',
  }),
  notifyCustomer: (id, payload) => apiCall(`/waitlist/${id}/send-notification`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
};

export const PromoGroup = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/settings/promo-groups${query}`);
  },
  get: (id) => apiCall(`/settings/promo-groups/${id}`),
  create: (promoGroupData) => apiCall('/settings/promo-groups', {
    method: 'POST',
    body: JSON.stringify(promoGroupData),
  }),
  update: (id, promoGroupData) => apiCall(`/settings/promo-groups/${id}`, {
    method: 'PUT',
    body: JSON.stringify(promoGroupData),
  }),
  delete: (id) => apiCall(`/settings/promo-groups/${id}`, {
    method: 'DELETE',
  }),
};

export const Role = {
  list: () => apiCall('/roles'),
  get: (id) => apiCall(`/roles/${id}`),
  create: (roleData) => apiCall('/roles', {
    method: 'POST',
    body: JSON.stringify(roleData),
  }),
  update: (id, roleData) => apiCall(`/roles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(roleData),
  }),
  delete: (id) => apiCall(`/roles/${id}`, {
    method: 'DELETE',
  }),
};

export const Permission = {
  list: () => apiCall('/permissions'),
  get: (id) => apiCall(`/permissions/${id}`),
  create: (permissionData) => apiCall('/permissions', {
    method: 'POST',
    body: JSON.stringify(permissionData),
  }),
  update: (id, permissionData) => apiCall(`/permissions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(permissionData),
  }),
  delete: (id) => apiCall(`/permissions/${id}`, {
    method: 'DELETE',
  }),
};

export const UserManagement = {
  list: () => apiCall('/users'),
  get: (id) => apiCall(`/users/${id}`),
  create: (userData) => apiCall('/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),
  update: (id, userData) => apiCall(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  }),
  delete: (id) => apiCall(`/users/${id}`, {
    method: 'DELETE',
  }),
};

export const RoleModelReport = {
  // Generate role model report
  generate: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/reports/role-model${query}`);
  },

  // Get role model report (alias for generate)
  getReport: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/reports/role-model${query}`);
  },
};

export const TargetGroups = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/target-groups${query}`);
  },
  get: (id) => apiCall(`/target-groups/${id}`),
  create: (data) => apiCall('/target-groups', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/target-groups/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/target-groups/${id}`, {
    method: 'DELETE',
  }),
};

export const TargetAddresses = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/target-addresses${query}`);
  },
  get: (id) => apiCall(`/target-addresses/${id}`),
  create: (data) => apiCall('/target-addresses', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/target-addresses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/target-addresses/${id}`, {
    method: 'DELETE',
  }),
  markReady: (id, isReady) => apiCall(`/target-addresses/${id}/ready`, {
    method: 'PATCH',
    body: JSON.stringify({ is_ready: Boolean(isReady) }),
  }),
  importCsv: async (groupId, file) => {
    if (!file) {
      throw new Error('Файл не выбран');
    }
    const token = getAuthToken();
    const base = await getApiBase();
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${base}/target-groups/${groupId}/import-addresses`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json();
  },
};

// Utility functions
export const ApiUtils = {
  // Convert item data to legacy format for backward compatibility
  itemToLegacy: (item) => {
    if (item.type === 'book_edition') {
      return {
        id: item.id,
        book_id: item.metadata?.book_id,
        edition_name: item.name,
        isbn: item.metadata?.isbn,
        format: item.metadata?.format,
        pages: item.metadata?.pages,
        price: item.price,
        is_active: item.is_active,
        created_date: item.created_date,
        updated_date: item.updated_date,
        created_by_id: item.created_by_id,
        created_by: item.created_by,
      };
    } else if (item.type === 'simple_product') {
      return {
        id: item.id,
        name: item.name,
        description: item.description,
        sku: item.sku,
        price: item.price,
        type: 'single',
        is_active: item.is_active,
        created_date: item.created_date,
        updated_date: item.updated_date,
        created_by_id: item.created_by_id,
        created_by: item.created_by,
      };
    }
    return item;
  },

  // Convert legacy data to item format
  legacyToItem: (legacyData, type) => {
    if (type === 'book_edition') {
      return {
        type: 'book_edition',
        name: legacyData.edition_name || legacyData.name,
        sku: legacyData.sku,
        description: legacyData.description,
        price: legacyData.price,
        is_active: legacyData.is_active,
        metadata: {
          book_id: legacyData.book_id,
          isbn: legacyData.isbn,
          format: legacyData.format,
          pages: legacyData.pages,
          author: legacyData.author,
          title: legacyData.title,
        },
      };
    } else if (type === 'simple_product') {
      return {
        type: 'simple_product',
        name: legacyData.name,
        sku: legacyData.sku,
        description: legacyData.description,
        price: legacyData.price,
        is_active: legacyData.is_active,
        metadata: {
          gtin: legacyData.gtin,
          brand: legacyData.brand,
          category: legacyData.category,
        },
      };
    }
    return legacyData;
  },

  // Validate item data
  validateItem: (itemData) => {
    const errors = [];

    if (!itemData.name || itemData.name.trim().length === 0) {
      errors.push('Item name is required');
    }

    if (!itemData.type || !['book_edition', 'simple_product', 'bundle'].includes(itemData.type)) {
      errors.push('Valid item type is required');
    }

    // ISBN и GTIN теперь необязательные, но если указаны - проверяем формат
    if (itemData.type === 'book_edition' && itemData.metadata?.isbn && !/^[0-9-]{10,13}$/.test(itemData.metadata.isbn)) {
      errors.push('Invalid ISBN format');
    }

    if (itemData.type === 'simple_product' && itemData.metadata?.gtin && !/^[0-9]{8,14}$/.test(itemData.metadata.gtin)) {
      errors.push('Invalid GTIN format');
    }

    if (itemData.price && (itemData.price < 0 || itemData.price > 999999.99)) {
      errors.push('Price must be between 0 and 999,999.99');
    }

    return errors;
  },
};

// Consent subsystem (admin)
export const ConsentDocuments = {
  list: ({ type, name, includeArchived = true } = {}) => {
    const qs = new URLSearchParams();
    if (type) qs.set('type', type);
    if (name) qs.set('name', name);
    qs.set('include_archived', String(includeArchived));
    return apiCall(`/consent-docs?${qs.toString()}`);
  },
  current: ({ type } = {}) => apiCall(`/consent-docs/current${type ? `?type=${encodeURIComponent(type)}` : ''}`),
  getById: (id) => apiCall(`/consent-docs/${id}`),
  update: (id, data) => apiCall(`/consent-docs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  publish: (data) => apiCall('/consent-docs', { method: 'POST', body: JSON.stringify(data) }),
  retire: ({ type, name, at }) => apiCall('/consent-docs/retire', {
    method: 'POST', body: JSON.stringify({ type, name, at })
  })
};

export const Consents = {
  list: (filters = {}, { limit = 50, offset = 0 } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    qs.set('limit', String(limit));
    qs.set('offset', String(offset));
    return apiCall(`/consents?${qs.toString()}`);
  },
  getById: (id) => apiCall(`/consents/${id}`),
  bySubmission: (submissionId) => apiCall(`/consents/submission/${submissionId}`),
  exportUrl: async (filters = {}, format = 'csv') => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    qs.set('format', format);
    const base = await getApiBase();
    return `${base}/consents/export?${qs.toString()}`;
  },
  exportBlob: async (filters = {}, format = 'csv') => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    qs.set('format', format);
    const base = await getApiBase();
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${base}/consents/export?${qs.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  },
  pseudonymize: (criteria) => apiCall('/consents/pseudonymize', {
    method: 'POST', body: JSON.stringify(criteria)
  })
};

export default {
  Items,
  Inventory,
  PrintRuns,
  AppSettings,
  Books,
  BookEditions,
  Products,
  ProductComponents,
  Warehouses,
  Users,
  Order,
  Customer,
  User,
  OrderHistory,
  InventoryMovement,
  InventoryMovementItem,
  InventoryMovementHistory,
  BookEdition,
  ProductComponent,
  Cycle,
  PrintRun,
  PrintRunHistory,
  Waitlist,
  PromoGroup,
  Role,
  Permission,
  UserManagement,
  RoleModelReport,
  ApiUtils,
};
